import { getApp } from 'firebase/app';

// Site analytics (Firebase / Google Analytics 4, using the project's existing measurement ID).
//
// Consent: nothing is loaded, stored or sent until the visitor accepts the cookie notice.
// Declining (or withdrawing later) switches collection off and removes the analytics cookies.
//
// Every counted visit is tagged with a user property "user_type":
//   visitor  a person who has never logged in on this device (the real customer numbers)
//   staff    a logged-in employee, or a device that has been used to log in before
// Employees can therefore be filtered out of customer reports, while their dashboard activity
// stays available. Register user_type as a user-scoped custom dimension in GA4 to use it.
//
// Analytics only runs on the live site, and its SDK is fetched after the page has loaded,
// so it never delays first paint.

const CONSENT_KEY = 'olivia_analytics_consent';
const STAFF_DEVICE_KEY = 'olivia_staff_device';

let startResolve;
const started = new Promise((resolve) => {
  startResolve = resolve;
});
let startRequested = false;
let identity = null; // { user, role } once the login state is known

const isLiveSite = () =>
  typeof window !== 'undefined' &&
  !['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

const afterLoad = () =>
  new Promise((resolve) => {
    const run = () => setTimeout(resolve, 1500);
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  });

// ---- consent (stored on the device) ----

/** 'granted', 'denied', or null when the visitor has not answered yet. */
export function getAnalyticsConsent() {
  try {
    const value = window.localStorage.getItem(CONSENT_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch (e) {
    return null;
  }
}

function clearAnalyticsCookies() {
  const hosts = [window.location.hostname, '.' + window.location.hostname.replace(/^www\./, '')];
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim();
    if (name === '_ga' || name.startsWith('_ga_') || name === '_gid') {
      document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
      hosts.forEach((host) => {
        document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=' + host;
      });
    }
  });
}

export function setAnalyticsConsent(granted) {
  try {
    window.localStorage.setItem(CONSENT_KEY, granted ? 'granted' : 'denied');
    if (!granted) window.localStorage.removeItem(STAFF_DEVICE_KEY);
  } catch (e) {
    /* storage blocked: the choice still applies for this page view */
  }

  if (granted) {
    if (startRequested) {
      started.then((loaded) => loaded && loaded.mod.setAnalyticsCollectionEnabled(loaded.analytics, true));
    } else {
      maybeStart();
    }
    return;
  }
  if (startRequested) {
    started.then((loaded) => loaded && loaded.mod.setAnalyticsCollectionEnabled(loaded.analytics, false));
  }
  clearAnalyticsCookies();
}

// ---- who is looking ----

function describe() {
  const { user, role } = identity;
  const consented = getAnalyticsConsent() === 'granted';
  if (user && consented) {
    try {
      window.localStorage.setItem(STAFF_DEVICE_KEY, '1');
    } catch (e) {
      /* ignore */
    }
  }
  let staffDevice = false;
  try {
    staffDevice = window.localStorage.getItem(STAFF_DEVICE_KEY) === '1';
  } catch (e) {
    /* ignore */
  }
  const properties = { user_type: user || staffDevice ? 'staff' : 'visitor' };
  if (user && role) properties.user_role = role;
  return { userId: user ? user.uid : null, properties };
}

async function load() {
  if (!isLiveSite()) return null;
  try {
    const { userId, properties } = describe();
    await afterLoad();
    if (getAnalyticsConsent() !== 'granted') return null;
    const mod = await import('firebase/analytics');
    if (!(await mod.isSupported())) return null;
    const config = { user_properties: properties };
    if (userId) config.user_id = userId;
    const analytics = mod.initializeAnalytics(getApp(), { config });
    return { mod, analytics };
  } catch (e) {
    return null;
  }
}

function maybeStart() {
  if (startRequested || !identity || getAnalyticsConsent() !== 'granted') return;
  startRequested = true;
  startResolve(load());
}

/**
 * Call whenever the login state is known (and again when it changes).
 * The first call after consent starts analytics, so the first page view is already tagged.
 */
export function setAnalyticsIdentity(user, role) {
  identity = { user, role };
  if (!startRequested) {
    maybeStart();
    return;
  }
  started.then((loaded) => {
    if (!loaded || getAnalyticsConsent() !== 'granted') return;
    const { userId, properties } = describe();
    loaded.mod.setUserId(loaded.analytics, userId);
    loaded.mod.setUserProperties(loaded.analytics, properties);
  });
}

// If the login check never reports back (offline, blocked), count the visit as a visitor anyway.
if (typeof window !== 'undefined') {
  setTimeout(() => {
    if (!identity) setAnalyticsIdentity(null, null);
  }, 6000);
}

export function track(eventName, params = {}) {
  if (getAnalyticsConsent() !== 'granted') return;
  started
    .then((loaded) => {
      if (loaded) loaded.mod.logEvent(loaded.analytics, eventName, params);
    })
    .catch(() => {});
}
